"use client";

import { VIEWED_LISTINGS_KEY } from "@/components/listings/listing-logged-out-seen-tracker";
import { Icon } from "@/components/ui/icon";
import { useCurrentUser } from "@/hooks/use-current-user";
import { api } from "@/lib/api-client";
import type { DetailedListingListItem } from "@/lib/types";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

export type ListingPreviewSeenOrContactedProps = {
  listing: DetailedListingListItem;
  forceSeen?: boolean;
};

export function ListingPreviewSeenOrContacted({
  listing,
  forceSeen,
}: ListingPreviewSeenOrContactedProps) {
  const t = useTranslations();

  const { user } = useCurrentUser();

  const [localSeen, setLocalSeen] = useState(false);

  const { data } = api.listings.hasSeenOrContacted.useQuery(
    {
      id: listing.id,
    },
    {
      enabled: !!user,
      retry: false,
    },
  );

  useEffect(() => {
    const current = localStorage.getItem(VIEWED_LISTINGS_KEY);
    if (!current) {
      return;
    }

    try {
      const parsedList = JSON.parse(current);

      if (Array.isArray(parsedList) && parsedList.includes(listing.id)) {
        setLocalSeen(true);
      }
    } catch {
      // ignore
    }
  }, []);

  if (!data?.seen && !data?.contacted && !localSeen && !forceSeen) {
    return null;
  }

  return (
    <div
      className={cn(
        "px-2.5 py-1 rounded-full flex items-center gap-1 text-[12px] font-medium",
        data?.contacted
          ? "bg-[#E8F9EF] text-[#00803A]"
          : "bg-muted text-muted-foreground",
      )}
    >
      <Icon
        icon={data?.contacted ? "CheckSolid" : "Eye"}
        className="size-3"
      />

      <span>
        {t(
          data?.contacted
            ? "saas.listingPreview.contacted"
            : "saas.listingPreview.seen",
        )}
      </span>
    </div>
  );
}
